import React, {ReactElement} from 'react';
import {useIntl, FormattedMessage} from 'react-intl';
import {cx} from '@emotion/css';
import {useLocale} from '../../../RootProvider/LocaleContext';
import Carousel from '../../DesignSystem/Carousel/Carousel';
import LayoutSection from '../../DesignSystem/Layout/LayoutSection/LayoutSection';
import {LayoutBackground} from '../../DesignSystem/Layout/LayoutSection/LayoutSection.types';
import {
  LayoutCircleType,
  LayoutCircleConfig,
} from '../../DesignSystem/Layout/LayoutSection/Circles/LayoutSectionCircles.types';
import SectionTitleAndSubtitle from '../../DesignSystem/SectionTitleAndSubtitle/SectionTitleAndSubtitle';
import {Alignment, HomePageCustomerStory} from '../../../types';
import ContentCard from '../../DesignSystem/ContentCard/ContentCard';
import {getPageRoute} from '../../../utils/routes/getPageRoute';

import Button, {ButtonVariant} from '../../DesignSystem/Buttons/Button/Button';
import styles from './CustomerStories.styles';
import DynamicLink from '../../DynamicLink/DynamicLink';
import Paragraph from '../../DesignSystem/Text/Paragraph/Paragraph';

interface Props {
  customerStories: HomePageCustomerStory[];
}

function CustomerStories({customerStories}: Props): ReactElement {
  const customerStoriesCircles: LayoutCircleConfig = {
    desktop: [
      {
        type: LayoutCircleType.DARK,
        style: {
          width: '54rem',
          top: '-37.5rem',
          right: '-20rem',
        },
      },
      {
        type: LayoutCircleType.DARK,
        style: {
          width: '15rem',
          top: '52.5rem',
          left: '2.6rem',
        },
      },
      {
        type: LayoutCircleType.DARK,
        style: {
          width: '7.2rem',
          top: '4.8rem',
          left: '13rem',
        },
      },
    ],
    tablet: [
      {
        type: LayoutCircleType.DARK,
        style: {
          width: '54rem',
          top: '-37.5rem',
          right: '-34rem',
        },
      },
      {
        type: LayoutCircleType.DARK,
        style: {
          width: '15rem',
          top: '52.5rem',
          left: '-8.6rem',
        },
      },
      {
        type: LayoutCircleType.DARK,
        style: {
          width: '7.2rem',
          top: '4.8rem',
          left: '3.2rem',
        },
      },
    ],
  };

  const {formatMessage} = useIntl();
  const locale = useLocale();

  return (
    <LayoutSection
      type={LayoutBackground.WHITE}
      innerClassName={cx(styles.customerStories)}
      circles={customerStoriesCircles}
    >
      <SectionTitleAndSubtitle
        title={formatMessage({id: 'homepage.customerStories.sectionTitleAndSubtitle.title'})}
        text={formatMessage({id: 'homepage.customerStories.sectionTitleAndSubtitle.text'})}
        alignment={Alignment.CENTER}
      />

      <Carousel>
        {customerStories.map(({id, name, quote, image, businessName, slug}) => (
          // Each Carousel direct child needs to be a div so that Slick can apply style="width: 100%; display: inline-block;" tabindex="-1" to it
          <DynamicLink
            className={cx(styles.link, 'unstyledLink')}
            key={id}
            href={getPageRoute('knowledge.customerStories.article', locale, {
              contentfulSlug: slug,
            })}
          >
            <ContentCard imgUrl={image?.file?.url}>
              <div className={cx(styles.contentWrapper)}>
                <Paragraph className={cx(styles.quote)}>{quote.quote}</Paragraph>
                <Paragraph className={cx(styles.business)}>
                  <span className={cx(styles.customer)}>{name},</span>&nbsp;
                  <Paragraph className={cx(styles.caseStudy)}>{businessName}</Paragraph>
                </Paragraph>
              </div>
            </ContentCard>
          </DynamicLink>
        ))}
      </Carousel>

      <div className={cx(styles.cta)}>
        <Button
          href={getPageRoute('knowledge.customerStories.home', locale)}
          variant={ButtonVariant.PRIMARY}
          ariaLabel={formatMessage({id: 'homepage.customerStories.cta'})}
        >
          <FormattedMessage id="homepage.customerStories.cta" />
        </Button>
      </div>
    </LayoutSection>
  );
}

export default CustomerStories;
